import api, { ApiResponse } from './api';

export interface User {
  id: number;
  name: string;
  email: string | null;
  username: string;
  image: string | null;
  user_type: number;
  user_status: number;
  building_id: number;
  package_handled: number;
  created_at?: string;
  updated_at?: string;
}

export interface UsersResponse {
  data: User[];
  next_page: number;
}

export interface InviteUserRequest {
  building_id: number;
  email: string;
  name: string;
  user_type: 1 | 2; // 1 = Site Manager, 2 = Floor Rep
}

export interface InviteUserResponse {
  invite_code: string;
  user: User;
}

export interface TransferableUser {
  id: number;
  name: string;
  email: string | null;
  username: string;
  image: string | null;
  user_type: number;
  user_status: number;
  building_id: number;
  building_name: string;
}

export interface TransferUserRequest {
  user_id: number;
  building_id: number;
  note?: string;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  username?: string;
  password?: string;
  send_new_password?: number;
}

export interface ChangeUserRoleRequest {
  user_id: number;
  new_type: 1 | 2;
  email?: string;
  username?: string;
  note?: string;
}

// User type labels
export const USER_TYPE_LABELS: Record<number, string> = {
  1: 'Site Manager',
  2: 'Floor Representative',
  3: 'Admin',
  4: 'Property Manager',
};

export const userService = {
  async getAllUsers(buildingId?: number): Promise<User[]> {
    const url = buildingId
      ? `/manage-user?building_id=${buildingId}`
      : '/manage-user';

    const response = await api.get<ApiResponse<UsersResponse>>(url);

    // CODE 200 = success with data, CODE 450 = no users found (empty result)
    if (response.data.CODE === 200 || response.data.CODE === 450) {
      const result = response.data.RESULT;
      if (result && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch users');
  },

  async inviteUser(data: InviteUserRequest): Promise<InviteUserResponse> {
    const response = await api.post<ApiResponse<InviteUserResponse>>('/manage-user', data);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to invite user');
  },

  async getUsersByType(userType: number, buildingId?: number): Promise<User[]> {
    const users = await this.getAllUsers(buildingId);
    return users.filter(user => user.user_type === userType);
  },

  async searchTransferable(
    buildingId: number,
    userType: 1 | 2,
    keyword: string,
  ): Promise<TransferableUser[]> {
    const params = new URLSearchParams({
      building_id: String(buildingId),
      user_type: String(userType),
      keyword,
    });

    const response = await api.get<ApiResponse<TransferableUser[]>>(
      `/search-transferable-users?${params.toString()}`,
    );

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    // 450 = no results
    if (response.data.CODE === 450) {
      return [];
    }

    throw new Error(response.data.MESSAGE || 'Failed to search users');
  },

  async transferUser(data: TransferUserRequest): Promise<User> {
    const response = await api.post<ApiResponse<User>>('/transfer-user', data);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to transfer user');
  },

  async changeUserRole(data: ChangeUserRoleRequest): Promise<User> {
    const response = await api.post<ApiResponse<User>>('/change-user-role', data);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to change user role');
  },

  async updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`/manage-user/${userId}`, data);

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to update user');
  },

  async updateUserStatus(userId: number, status: number): Promise<User> {
    const response = await api.post<ApiResponse<User>>('/manage-user/update-status', {
      id: userId,
      status,
    });

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to update user status');
  },
};

export default userService;
